import React, { useState, useEffect, useRef } from "react";
import { OrderService } from "../services/api/order.service";
import dandarLogo from "../assets/images/dandar-logo.png";

interface ProductDetail {
  productId: string;
  quantity: number;
  pack: string;
  hsnNumber: string;
  cgst: string;
  sgst: string;
  pricePerUnit: string;
  productName: string;
  discount: string;
  finalSellingPrice: string;
  mrp: string;
  batchNumber: string;
  expiryDate: string;
}

interface ClinicDetails {
  clinicName: string;
  clinicMobileNumber: string;
  clinicAddress: string;
  profileImage: string;
  clinicSupervisorName: string;
  clinicPincode: string;
  drugLicenseNumber: string;
  gstIn: string;
  fssaiNumber: string;
}

interface PatientDetails {
  patientName: string;
  patientProfileImage: string;
  patientMobileNumber: string;
  patientAddress: string;
  patientPincode: string;
}

interface BillData {
  orderId: number;
  invoiceNumber: string;
  doctorName: string | null;
  paymentAmount: string;
  orderDate: string;
  patientDetails: PatientDetails;
  productDetails: ProductDetail[];
  clinicDetails: ClinicDetails;
  amountInWords: string;
  deliveryCharges?: number;
  coinsUsed?: number
}

interface ApiResponse {
  success: boolean;
  message: string;
  billData: BillData;
}

interface BillProps {
  orderId: number;
  orderType: string;
  onClose?: () => void;
  generatedPrescription?: any;
  setGeneratedPrescription?: React.Dispatch<React.SetStateAction<any>>;
}

const Bill: React.FC<BillProps> = ({ orderId, orderType, onClose }) => {
  const [billData, setBillData] = useState<BillData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string>("");
  const componentRef = useRef<HTMLDivElement>(null);

  // Items per page for A5 format (reduced from A4)
  const ITEMS_PER_PAGE = 8;

  // Convert image to base64 for reliable printing
  const convertImageToBase64 = (imageSrc: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = img.width;
        canvas.height = img.height;

        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } else {
          reject("Canvas context not available");
        }
      };

      img.onerror = () => {
        resolve(imageSrc);
      };

      img.src = imageSrc;
    });
  };

  // Split products into pages
  const getPagedProducts = () => {
    if (!billData?.productDetails) return [];

    const pages: ProductDetail[][] = [];
    for (let i = 0; i < billData.productDetails.length; i += ITEMS_PER_PAGE) {
      pages.push(billData.productDetails.slice(i, i + ITEMS_PER_PAGE));
    }
    return pages;
  };

  // Enhanced print function for A5 format with pagination
  const handleAlternativePrint = () => {
    if (!componentRef.current || !billData) return;

    const printWindow = window.open("", "_blank", "width=600,height=800");
    if (!printWindow) {
      alert("Unable to open print window. Please check your popup blocker.");
      return;
    }

    const stylesheets = Array.from(document.styleSheets);
    let allStyles = "";

    stylesheets.forEach((stylesheet) => {
      try {
        if (stylesheet.cssRules) {
          Array.from(stylesheet.cssRules).forEach((rule) => {
            allStyles += rule.cssText + "\n";
          });
        }
      } catch (e) {
        console.warn("Could not access stylesheet:", e);
      }
    });

    const printContent = componentRef.current.innerHTML;

    let modifiedContent = printContent;
    if (logoDataUrl) {
      modifiedContent = printContent.replace(
        /src="[^"]*dandar-logo[^"]*"/g,
        `src="${logoDataUrl}"`
      );
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${billData.invoiceNumber || orderId}</title>
          <meta charset="utf-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.3;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            /* Page break utilities */
            .page-break {
              page-break-after: always;
              break-after: page;
            }
            
            .page-break-inside-avoid {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            /* Tailwind-like utility classes */
            .border-2 { border: 2px solid black !important; }
            .border-b-2 { border-bottom: 2px solid black !important; }
            .border-r { border-right: 1px solid black !important; }
            .border-black { border-color: black !important; }
            .border-b { border-bottom: 1px solid black !important; }
            .border-gray-300 { border-color: #d1d5db !important; }
            .p-2 { padding: 6px !important; }
            .p-3 { padding: 10px !important; }
            .pt-2 { padding-top: 6px !important; }
            .pb-5 { padding-bottom: 16px !important; }
            .pr-6 { padding-right: 20px !important; }
            .mt-2 { margin-top: 6px !important; }
            .mt-4 { margin-top: 12px !important; }
            .mb-2 { margin-bottom: 6px !important; }
            .mb-4 { margin-bottom: 12px !important; }
            .ml-8 { margin-left: 28px !important; }
            .text-sm { font-size: 12px !important; }
            .text-xs { font-size: 10px !important; }
            .text-xl { font-size: 16px !important; }
            .font-bold { font-weight: bold !important; }
            .font-medium { font-weight: 500 !important; }
            .text-center { text-align: center !important; }
            .text-right { text-align: right !important; }
            .text-left { text-align: left !important; }
            .text-start { text-align: left !important; }
            .flex { display: flex !important; }
            .justify-between { justify-content: space-between !important; }
            .justify-end { justify-content: flex-end !important; }
            .items-center { align-items: center !important; }
            .items-start { align-items: flex-start !important; }
            .items-end { align-items: flex-end !important; }
            .flex-col { flex-direction: column !important; }
            .gap-3 { gap: 10px !important; }
            .w-full { width: 100% !important; }
            .w-48 { width: 160px !important; }
            .h-12 { height: 40px !important; }
            .h-8 { height: 28px !important; }
            
            /* Width percentages */
            .w-\\[70\\%\\] { width: 70% !important; }
            .w-\\[30\\%\\] { width: 30% !important; }
            .w-\\[45\\%\\] { width: 45% !important; }
            .w-\\[33\\%\\] { width: 33% !important; }
            .w-\\[22\\%\\] { width: 22% !important; }

            /* Table styles */
            table {
              border-collapse: collapse !important;
              width: 100% !important;
            }
            
            th, td {
              border: 1px solid black !important;
              padding: 3px !important;
              vertical-align: top !important;
              font-size: 10px !important;
            }
            
            thead th {
              background-color: white !important;
              font-weight: bold !important;
            }

            /* Image styles */
            img {
              max-width: 100% !important;
              height: auto !important;
              display: block !important;
            }

            /* Print specific styles for A5 */
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                size: A5;
                margin: 8mm;
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              .page-break {
                page-break-after: always;
                break-after: page;
              }
              
              .page-break-inside-avoid {
                page-break-inside: avoid;
                break-inside: avoid;
              }
            }

            /* Additional styles from current document */
            ${allStyles}
          </style>
        </head>
        <body>
          ${modifiedContent}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 500);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  useEffect(() => {
    fetchBillData();
    convertImageToBase64(dandarLogo)
      .then(setLogoDataUrl)
      .catch(() => setLogoDataUrl(dandarLogo));
  }, []);

  const fetchBillData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const result: ApiResponse = await OrderService.generateBill(
        orderId,
        orderType
      );

      if (result.success) {
        setBillData(result.billData);
      } else {
        throw new Error(result.message || "Failed to fetch bill data");
      }

      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
      console.log("Error", err);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDayName = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  };

  const calculateSubtotal = (): string => {
    if (!billData?.productDetails) return "0.00";
    if (orderType === "OFFLINE") {
      return billData.productDetails
        .reduce(
          (sum, item) =>
            sum +
            (Number(item.mrp) / Number(item.pack ? item.pack : 1)) *
            parseFloat(item.quantity.toString()),
          0
        )
        .toFixed(2);
    }
    return billData.productDetails
      .reduce((sum, item) => sum + Number(item.quantity) * Number(item.mrp), 0)
      .toFixed(2);
  };

  const calculateDiscount = (): string => {
    if (!billData?.productDetails) return "0.00";

    if (orderType === "OFFLINE") {
      const amount = calculateSubtotal();
      const payableAmount = billData.paymentAmount;

      return (Number(amount) - Number(payableAmount)).toFixed(2);
    }

    return billData.productDetails
      .reduce(
        (sum, item) =>
          sum +
          (parseFloat(item.discount) *
            Number(item.mrp) *
            parseFloat(item.quantity.toString())) /
          100,
        0
      )
      .toFixed(2);
  };

  const safeParseFloat = (value: string | number): number => {
    const parsed = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(parsed) ? 0 : parsed;
  };

  const getTaxBreakup = (): string => {
    if (!billData?.productDetails) return "No tax details available";

    const taxMap = new Map<number, { cgst: number; sgst: number }>();

    billData.productDetails.forEach((item) => {
      const cgstRate = parseFloat(item.cgst);
      const sgstRate = parseFloat(item.sgst);
      const gstRate = cgstRate + sgstRate;
      const itemAmount = safeParseFloat(item.finalSellingPrice);
      const cgstAmount = (itemAmount * cgstRate) / 100;
      const sgstAmount = (itemAmount * sgstRate) / 100;

      if (taxMap.has(gstRate)) {
        const current = taxMap.get(gstRate)!;
        taxMap.set(gstRate, {
          cgst: current.cgst + cgstAmount,
          sgst: current.sgst + sgstAmount,
        });
      } else {
        taxMap.set(gstRate, {
          cgst: cgstAmount,
          sgst: sgstAmount,
        });
      }
    });

    const sortedRates = Array.from(taxMap.entries()).sort(
      ([rateA], [rateB]) => rateB - rateA
    );

    const taxStrings = sortedRates.map(([rate, amounts]) => {
      const halfRate = rate / 2;
      return `${halfRate}%CGST + ${halfRate}%SGST = ${rate}% (₹${amounts.cgst.toFixed(
        2
      )} + ₹${amounts.sgst.toFixed(2)})`;
    });

    const totalCGST = Array.from(taxMap.values()).reduce(
      (sum, { cgst }) => sum + cgst,
      0
    );
    const totalSGST = Array.from(taxMap.values()).reduce(
      (sum, { sgst }) => sum + sgst,
      0
    );
    const totalTax = totalCGST + totalSGST;

    taxStrings.push(
      `Total GST: ₹${totalTax.toFixed(2)} (CGST: ₹${totalCGST.toFixed(
        2
      )} + SGST: ₹${totalSGST.toFixed(2)})`
    );

    return taxStrings.join(" | ");
  };

  const renderBillHeader = () => (
    <>
      <div className="border-b-2 border-black p-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3 w-[70%]">
            <div className="flex items-center gap-3">
              <img
                src={logoDataUrl || dandarLogo}
                alt="Dandar Logo"
                className="h-12"
                style={{ maxHeight: "40px", width: "auto" }}
              />
            </div>
          </div>
          <div className="text-start text-sm w-[30%]">
            <div className="font-bold">Patient Details</div>
            <div>
              Patient Name:{" "}
              <span className="font-bold">
                {billData!.patientDetails.patientName}
              </span>
            </div>
            <div>
              Patient Address:
              <span className="font-bold">
                {billData!.patientDetails.patientAddress || "N/A"}
              </span>
            </div>
            <div>
              Dr Name:{" "}
              <span className="font-bold">
                {billData!.doctorName || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-2 text-sm">
          <div className="font-bold">
            {billData!.clinicDetails.clinicName}
          </div>
          <div>{billData!.clinicDetails.clinicAddress}</div>
          <div>
            Phone: {billData!.clinicDetails.clinicMobileNumber}
          </div>
        </div>
      </div>

      <div className="border-b-2 border-black p-3">
        <div className="flex justify-between">
          <div className="text-sm">
            <div>
              <strong>GSTIN:</strong> {billData!.clinicDetails.gstIn}
            </div>
            <div>
              <strong>D.L.No.:</strong>{" "}
              {billData!.clinicDetails.drugLicenseNumber}
            </div>
            <div>
              <strong>FSSAI No.:</strong>{" "}
              {billData!.clinicDetails?.fssaiNumber}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">GST INVOICE</div>
          </div>
          <div className="text-sm text-right">
            <div>
              <strong>Invoice No.:</strong> {billData!.invoiceNumber}{" "}
              Date: {formatDate(billData!.orderDate)}
            </div>
            <div>
              <strong>Time:</strong> {formatTime(billData!.orderDate)}
            </div>
            <div>
              <strong>Day:</strong> {getDayName(billData!.orderDate)}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderProductTable = (products: ProductDetail[], isLastPage: boolean) => (
    <div className="border-b-2 border-black">
      <table className="w-full text-sm">
        <thead className="border-b border-black">
          <tr>
            <th className="border-r border-black p-2 text-left">SN.</th>
            <th className="border-r border-black p-2 text-left">PRODUCT NAME</th>
            <th className="border-r border-black p-2 text-left">PACK</th>
            <th className="border-r border-black p-2 text-left">HSN</th>
            <th className="border-r border-black p-2 text-left">BATCH</th>
            <th className="border-r border-black p-2 text-left">EXPIRY</th>
            <th className="border-r border-black p-2 text-left">QTY</th>
            <th className="border-r border-black p-2 text-left">MRP</th>
            {orderType === "OFFLINE" && (
              <th className="border-r border-black p-2 text-left">Per Unit Price</th>
            )}
            <th className="border-r border-black p-2 text-left">DIS%</th>
            <th className="border-r border-black p-2 text-left">CGST</th>
            <th className="border-r border-black p-2 text-left">SGST</th>
            <th className="p-2 text-left">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {products.map((item: ProductDetail, index: number) => (
            <tr
              key={`${item.productId}-${index}`}
              className="border-b border-gray-300"
            >
              <td className="border-r border-black p-2">
                {billData!.productDetails.indexOf(item) + 1}.
              </td>
              <td className="border-r border-black p-2 font-medium">
                {item.productName}
              </td>
              <td className="border-r border-black p-2">{item.pack}</td>
              <td className="border-r border-black p-2">{item.hsnNumber}</td>
              <td className="border-r border-black p-2">{item.batchNumber}</td>
              <td className="border-r border-black p-2">
                {item.expiryDate
                  ? (() => {
                    const date = new Date(item.expiryDate);
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = String(date.getFullYear()).slice(-2);
                    return `${month}/${year}`;
                  })()
                  : "N/A"}
              </td>
              <td className="border-r border-black p-2 text-center">
                {item.quantity}.0
              </td>
              <td className="border-r border-black p-2 text-right">
                {item?.mrp != null ? safeParseFloat(item.mrp).toFixed(2) : ""}
              </td>
              {orderType === "OFFLINE" && (
                <td className="border-r border-black p-2 text-right">
                  {(Number(item.mrp) / Number(item.pack ? item.pack : 1)).toFixed(2)}
                </td>
              )}
              <td className="border-r border-black p-2 text-right">
                {safeParseFloat(item.discount).toFixed(2)}
              </td>
              <td className="border-r border-black p-2 text-center">
                {safeParseFloat(item.cgst).toFixed(2)}
              </td>
              <td className="border-r border-black p-2 text-center">
                {safeParseFloat(item.sgst).toFixed(2)}
              </td>
              <td className="p-2 text-right">
                {safeParseFloat(item.finalSellingPrice).toFixed(2)}
              </td>
            </tr>
          ))}
          {/* Add empty rows only on last page to maintain consistent height */}
          {isLastPage && Array.from({
            length: Math.max(0, ITEMS_PER_PAGE - products.length),
          }).map((_, index: number) => (
            <tr key={`empty-${index}`} className="border-b border-gray-300">
              <td className="border-r border-black p-2 h-8">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              {orderType === "OFFLINE" && (
                <td className="border-r border-black p-2">&nbsp;</td>
              )}
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="border-r border-black p-2">&nbsp;</td>
              <td className="p-2">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderFooter = () => (
    <>
      <div className="border-b-2 border-black p-2 text-xs">
        <div>{getTaxBreakup()}</div>
      </div>

      <div className="p-3">
        <div className="flex justify-between w-full">
          <div className="text-sm w-[45%]">
            <div className="font-bold">Terms & Conditions</div>
            <div>Goods once sold will not be taken back.</div>
            <div>Please check medicines before leaving the counter.</div>
            <div>All disputes subject to Ranchi Jurisdiction only.</div>
            <div>NO CASH REFUND.</div>
          </div>

          <div className="flex justify-end items-end pb-5 w-[33%]">
            <div className="text-center text-xs pr-6">
              For DANDAR PHARMACEUTICALS PVT. LTD.
            </div>
          </div>

          <div className="flex flex-col items-center justify-center w-[22%]">
            <div className="mb-4 text-sm">
              <div className="flex justify-between w-48">
                <span>SUB TOTAL</span>
                <span>{calculateSubtotal()}</span>
              </div>
              <div className="flex justify-between w-48">
                <span>Discount</span>
                <span>{calculateDiscount()}</span>
              </div>
              <div className="flex justify-between w-48">
                <span>Coins Used</span>
                <span>{billData!.coinsUsed}</span>
              </div>
            </div>

            <div className="border-t-2 border-black pt-2 w-full">
              <div className="text-xs text-right">
                <span>GRAND TOTAL</span>
                <span className="ml-8">
                  {safeParseFloat(billData!.paymentAmount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex mt-4 justify-between w-full">
          <div className="text-sm font-bold w-[45%]">
            Rs. {billData!.amountInWords}
          </div>
          <div className="mb-2 text-right w-[33%]">
            <span className="text-xs px-6">Authorised Signatory</span>
          </div>
          <div className="w-[22%]"></div>
        </div>
      </div>
    </>
  );

  const pagedProducts = getPagedProducts();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 opacity-75"
            onClick={onClose}
          ></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4 no-print">
            <button
              onClick={onClose}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p>Loading bill data...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-red-500">
                <p>Error loading bill data: {error}</p>
                <button
                  onClick={fetchBillData}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              </div>
            ) : !billData ? (
              <div className="p-8 text-center">
                <p>No bill data available</p>
              </div>
            ) : (
              <div ref={componentRef}>
                {pagedProducts.map((pageProducts, pageIndex) => (
                  <div
                    key={pageIndex}
                    className={`border-2 border-black ${pageIndex < pagedProducts.length - 1 ? "page-break" : ""
                      }`}
                  >
                    {renderBillHeader()}
                    {renderProductTable(pageProducts, pageIndex === pagedProducts.length - 1)}
                    {pageIndex === pagedProducts.length - 1 && renderFooter()}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse no-print">
            <button
              onClick={handleAlternativePrint}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Print
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bill;
